/**
 * App-wide language for non-React callers (lib/, hooks without useLanguage).
 *
 * Initialized synchronously at module load from localStorage / ?lang (same
 * keys/rules as LanguageContext). LanguageProvider keeps the ref in sync on
 * change.
 */
export type AppLanguage = "no" | "en" | "uk" | "ar" | "zh";

/** Same keys as LanguageContext — single source of truth. */
export const LANGUAGE_STORAGE_KEY = "language";
export const LANGUAGE_USER_SET_KEY = "language_user_set";

const VALID: ReadonlySet<string> = new Set(["no", "en", "uk", "ar", "zh"]);

function readStoredLanguageRaw(): AppLanguage | null {
  if (typeof window === "undefined") return null;
  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return saved && VALID.has(saved) ? (saved as AppLanguage) : null;
}

function readSessionLanguageFromUrl(): AppLanguage | null {
  if (typeof window === "undefined") return null;
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang && VALID.has(lang) ? (lang as AppLanguage) : null;
}

/**
 * Resolve initial language from localStorage / ?lang with the same rules as
 * LanguageProvider's useState initializer (incl. legacy auto-detect reset).
 *
 * Priority:
 * 1. explicit manual user choice in localStorage
 * 2. valid ?lang= session override (not persisted)
 * 3. Norwegian default
 *
 * user_set only shields a valid stored choice; with no valid stored value,
 * ?lang may apply.
 */
export function resolveInitialAppLanguage(): AppLanguage {
  if (typeof window === "undefined") return "no";

  const userSet = window.localStorage.getItem(LANGUAGE_USER_SET_KEY);
  const saved = readStoredLanguageRaw();
  const sessionLang = readSessionLanguageFromUrl();

  if (userSet && saved) {
    return saved;
  }

  if (!userSet && saved && saved !== "no") {
    window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    return sessionLang ?? "no";
  }

  return sessionLang ?? saved ?? "no";
}

/** Module-load init — first formatter call on cold load uses stored language. */
let currentLanguage: AppLanguage =
  typeof window !== "undefined" ? resolveInitialAppLanguage() : "no";

/** Called by LanguageProvider whenever language changes. */
export function setAppLanguage(language: AppLanguage): void {
  currentLanguage = language;
}

/**
 * Current UI language for formatters in lib/ and hooks (no React subscription).
 * Display components must also call useLanguage() so they re-render on switch.
 */
export function getAppLanguage(): AppLanguage {
  if (typeof window !== "undefined") {
    const saved = readStoredLanguageRaw();
    if (saved && saved !== currentLanguage) {
      currentLanguage = saved;
    }
  }
  return currentLanguage;
}
